import { useState } from "react";
import { Link } from "react-router-dom";
import ScrollReveal from "../../../components/feature/ScrollReveal";
import { useColumnData } from "../../../lib/useMockData";

export default function ColumnList() {
  const { categories, columns, columnPageMeta } = useColumnData();
  const [activeCat, setActiveCat] = useState("all");

  const featured = columns.filter((c) => c.isFeatured);
  const popular = columns.filter((c) => c.isPopular).slice(0, 4);
  const newArticles = columns.filter((c) => c.isNew).slice(0, 4);
  const yokohamaRelated = columns
    .filter((c) => c.categoryId === "date" || c.categoryId === "compare")
    .slice(0, 3);
  const giftRelated = columns
    .filter((c) => c.categoryId === "gift" || c.categoryId === "propose")
    .slice(0, 3);

  const filtered =
    activeCat === "all"
      ? columns
      : columns.filter((c) => c.categoryId === activeCat);

  const ArticleCard = ({
    article,
    size = "normal",
  }: {
    article: (typeof columns)[0];
    size?: "large" | "normal";
  }) => (
    <Link
      to={`/column/${article.slug}`}
      className="group block card-luxury h-full flex flex-col"
    >
      <div className="aspect-[3/2] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-4 md:p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-medium tracking-ultra text-airdoa-gold uppercase">
            {article.category}
          </span>
          <span className="text-[10px] text-airdoa-gray">{article.date}</span>
          {article.isNew && (
            <span className="px-1.5 py-0.5 text-[9px] font-medium text-white bg-airdoa-navy rounded">
              NEW
            </span>
          )}
        </div>
        <h3
          className={`font-serif font-semibold text-airdoa-navy leading-snug group-hover:text-airdoa-gold transition-colors ${
            size === "large" ? "text-base md:text-lg" : "text-sm"
          }`}
        >
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-airdoa-gray line-clamp-2 flex-1">
          {article.excerpt}
        </p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-airdoa-gray">
            {article.readTime}で読めます
          </span>
          <span className="text-xs text-airdoa-gold font-medium">
            {columnPageMeta.readMore}
            <i className="ri-arrow-right-line ml-1" />
          </span>
        </div>
      </div>
    </Link>
  );

  const SectionTitle = ({
    label,
    title,
  }: {
    label: string;
    title: string;
  }) => (
    <div className="text-center mb-8 md:mb-10">
      <p className="section-label mb-2">{label}</p>
      <h2 className="section-title">{title}</h2>
    </div>
  );

  return (
    <>
      {/* 特集記事 */}
      {featured.length > 0 && (
        <section className="py-16 md:py-20 bg-airdoa-white">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <ScrollReveal direction="up">
                <SectionTitle label="FEATURED" title={columnPageMeta.featuredTitle} />
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {featured.map((article, idx) => (
                  <ScrollReveal
                    key={article.slug}
                    direction="up"
                    delay={idx * 100}
                  >
                    <ArticleCard article={article} size="large" />
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 新着記事 */}
      {newArticles.length > 0 && (
        <section className="py-16 md:py-20 bg-airdoa-cream">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <ScrollReveal direction="up">
                <SectionTitle label="NEW" title={columnPageMeta.newTitle} />
              </ScrollReveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {newArticles.map((article, idx) => (
                  <ScrollReveal
                    key={article.slug}
                    direction="up"
                    delay={idx * 80}
                  >
                    <ArticleCard article={article} />
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 人気記事 */}
      {popular.length > 0 && (
        <section className="py-16 md:py-20 bg-airdoa-white">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <ScrollReveal direction="up">
                <SectionTitle label="POPULAR" title={columnPageMeta.popularTitle} />
              </ScrollReveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {popular.map((article, idx) => (
                  <ScrollReveal
                    key={article.slug}
                    direction="up"
                    delay={idx * 80}
                  >
                    <ArticleCard article={article} />
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* カテゴリタブ + 全記事一覧 */}
      <section className="py-16 md:py-20 bg-airdoa-cream">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal direction="up">
              <div className="text-center mb-10">
                <p className="section-label mb-2">ALL ARTICLES</p>
                <h2 className="section-title">記事一覧</h2>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={100}>
              <div className="flex flex-wrap justify-center gap-2 mb-10">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCat(cat.id)}
                    className={`px-4 py-2 text-xs font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                      activeCat === cat.id
                        ? "bg-airdoa-navy text-white"
                        : "bg-white text-airdoa-gray border border-airdoa-gray-light/50 hover:border-airdoa-gold hover:text-airdoa-navy"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((article, idx) => (
                <ScrollReveal
                  key={article.slug}
                  direction="up"
                  delay={idx * 60}
                >
                  <ArticleCard article={article} />
                </ScrollReveal>
              ))}
            </div>

            {filtered.length === 0 && (
              <p className="text-center text-sm text-airdoa-gray py-12">
                このカテゴリの記事はまだありません。
              </p>
            )}
          </div>
        </div>
      </section>

      {/* 横浜関連おすすめ */}
      {yokohamaRelated.length > 0 && (
        <section className="py-16 md:py-20 bg-airdoa-navy text-white">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <ScrollReveal direction="up">
                <div className="text-center mb-10">
                  <p className="text-xs font-medium tracking-ultra text-airdoa-gold uppercase mb-2">
                    YOKOHAMA
                  </p>
                  <h2 className="text-2xl md:text-3xl font-serif font-semibold leading-tight">
                    横浜ヘリクルージングの記事
                  </h2>
                </div>
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {yokohamaRelated.map((article, idx) => (
                  <ScrollReveal
                    key={article.slug}
                    direction="up"
                    delay={idx * 100}
                  >
                    <Link
                      to={`/column/${article.slug}`}
                      className="group block bg-airdoa-navy-light rounded-lg border border-white/10 overflow-hidden h-full flex flex-col"
                    >
                      <div className="aspect-[3/2] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4 flex-1">
                        <span className="text-[10px] font-medium tracking-ultra text-airdoa-gold uppercase">
                          {article.category}
                        </span>
                        <h3 className="mt-1.5 text-sm font-serif font-semibold text-white leading-snug group-hover:text-airdoa-gold transition-colors">
                          {article.title}
                        </h3>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ギフト・記念日関連 */}
      {giftRelated.length > 0 && (
        <section className="py-16 md:py-20 bg-airdoa-white">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <ScrollReveal direction="up">
                <div className="text-center mb-10">
                  <p className="section-label mb-2">GIFT & ANNIVERSARY</p>
                  <h2 className="section-title">ギフト・記念日の記事</h2>
                </div>
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {giftRelated.map((article, idx) => (
                  <ScrollReveal
                    key={article.slug}
                    direction="up"
                    delay={idx * 100}
                  >
                    <Link
                      to={`/column/${article.slug}`}
                      className="group block card-luxury h-full flex flex-col"
                    >
                      <div className="aspect-[3/2] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4 flex-1">
                        <span className="text-[10px] font-medium tracking-ultra text-airdoa-gold uppercase">
                          {article.category}
                        </span>
                        <h3 className="mt-1.5 text-sm font-serif font-semibold text-airdoa-navy leading-snug group-hover:text-airdoa-gold transition-colors">
                          {article.title}
                        </h3>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
